import { AxiosError } from 'axios';
import { useMemo, useRef, useState } from 'react';
import useSWR, { KeyedMutator, SWRConfiguration } from 'swr';

import { DEFAULT_SWR_SETTINGS } from '../config';

import { useErrorSnackbarEffect, useGlobalSwr, usePrevious } from './util';

// eslint-disable-next-line
type ClassType = { new (...args: any[]): any; [key: string]: any };

// eslint-disable-next-line
type Client = ClassType | Record<string, (...args: any[]) => unknown>;
type RevalidateOtherClientType = readonly Client[];

type UseApiEffectFunctionsReturn<C extends Client, N extends (keyof C)[]> = { [K in N[number]]: C[K] };

interface UseApiEffectStateReturn {
  isLoading: boolean;
  error?: Optional<Error | AxiosError>;
  hasError: boolean;
}

type KeysOfClient<T> = T extends Client ? keyof T : never;
type KeysOfClients<T extends readonly Client[]> = KeysOfClient<T[number]>;

type UseApiEffectRevalidateOtherSettings<RC extends RevalidateOtherClientType> = {
  client: RC;
  methods: KeysOfClients<RC>[];
};

interface UseApiEffectSettings<C, RC extends RevalidateOtherClientType> {
  revalidate?: (keyof C)[];
  revalidateOtherClient?: UseApiEffectRevalidateOtherSettings<RC>;
  suppressErrors?: boolean; // Do not show error snackbar
  rethrow?: boolean; // Do not swallow exception (if true)
}

/**
 * A lightweight version of `useAwsEffect` for internal API calls.
 *
 * Example:
 * ```ts
 * const client = {
 *   doSomething: (arg: string) => console.log(arg);
 * };
 *
 * const { doSomething } = useApiEffect(client, ['doSomething']);
 * ```
 *
 * @param client - service client to use with this hook
 * @param methods - list of methods/functions to expose to the hook
 */
export const useApiEffect = <C extends Client, RC extends RevalidateOtherClientType, N extends (keyof C)[]>(
  client: C,
  methods: (keyof C)[],
  settings?: UseApiEffectSettings<C, RC>,
): UseApiEffectFunctionsReturn<C, N> & UseApiEffectStateReturn => {
  const [loadingStates, setLoadingStates] = useState({});
  const [error, setError] = useState<Optional<Error | AxiosError>>(null);

  const { mutateRelated } = useGlobalSwr();

  const isLoading = Object.values(loadingStates).some(Boolean);

  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const definitions = useMemo(
    () =>
      methods.reduce(
        (memo, method) => ({
          ...memo,
          // eslint-disable-next-line
          [method]: async (...args: any[]) => {
            try {
              setLoadingStates((prev) => ({ ...prev, [method]: true }));

              const callable = client[method] as C[string];
              const result = await callable.call(client, ...args);

              mutateRelated(method);

              if (settingsRef.current?.revalidate) {
                settingsRef.current.revalidate.forEach((key) => mutateRelated(key));
              }
              if (settingsRef.current?.revalidateOtherClient) {
                settingsRef.current.revalidateOtherClient.methods.forEach((key) => mutateRelated(key));
              }

              return result;
            } catch (e) {
              console.error(e);
              setError(e as Error | AxiosError);
              if (settingsRef.current?.rethrow) {
                throw e;
              }
            } finally {
              setLoadingStates((prev) => ({ ...prev, [method]: false }));
            }
          },
        }),
        {} as UseApiEffectFunctionsReturn<C, N>,
      ),
    [client, mutateRelated, methods.join(',')], // methods content is stable; join avoids array identity churn
  );

  useErrorSnackbarEffect(settings?.suppressErrors ? undefined : error);

  return {
    ...definitions,
    isLoading,
    error,
    hasError: !!error,
  };
};

type AwaitedReturn<C extends Client, N extends keyof C> =
  ReturnType<C[N]> extends Promise<infer X> ? X : ReturnType<C[N]>;

interface UseApiGetterSettings<C extends Client, N extends keyof C, R = AwaitedReturn<C, N>> {
  defaultValue?: Optional<R>;
  /**
   * Optional mapper applied to the resolved value (the API output, or the
   * `defaultValue` while loading/disabled). Its return type becomes the type of
   * `data`. Note the input is `Optional` because the value can be undefined
   * before the first load resolves or after an error.
   */
  transform?: (value: Optional<R>) => unknown;
  enable?: boolean;
  swrOverrides?: SWRConfiguration;
  suppressErrors?: boolean;
}

/**
 * Resolves the type of `data` based on the provided settings:
 * - when a `transform` is supplied, `data` is its return type;
 * - otherwise when a non-nullish `defaultValue` is supplied, `data` is guaranteed
 *   to be defined and loses its `Optional` wrapper;
 * - otherwise `data` stays `Optional`.
 */
type ResolvedData<C extends Client, N extends keyof C, S> = S extends {
  transform: (...args: never[]) => infer T;
}
  ? T
  : S extends { defaultValue: infer D }
    ? [D] extends [NonNullable<D>]
      ? AwaitedReturn<C, N>
      : Optional<AwaitedReturn<C, N>>
    : Optional<AwaitedReturn<C, N>>;

interface UseApiSetterReturn<D, Raw = D> {
  data: D;
  error?: Error | AxiosError;
  hasError: boolean;
  isLoading: boolean;
  mutate: KeyedMutator<Optional<Raw>>;
}

/**
 * A lightweight version of `useAwsGetter` for internal services.
 *
 * Example:
 * ```ts
 * const client = {
 *   loadSomething: (arg: number) => Promise.resolve([1, arg, 3]);
 * };
 *
 * const {} = useApiGetter(client, 'loadSomething', [2]);
 * ```
 *
 * @param client - api client
 * @param name - method or function name to use as a getter
 * @param args - arguments that will be passed to the method/function.
 *               Please note the call is skipped in case arity does not match.
 *               This is to simplify state-driven loads.
 * @param settings - getter settings and overrides
 * @returns
 */
export const useApiGetter = <
  C extends Client,
  N extends keyof C,
  S extends UseApiGetterSettings<C, N> = UseApiGetterSettings<C, N>,
>(
  client: C,
  name: N,
  args: Parameters<C[N]>,
  settings?: S,
): UseApiSetterReturn<ResolvedData<C, N, S>, AwaitedReturn<C, N>> => {
  const callable = client[name];

  const enable = settings?.enable === undefined || !!settings?.enable;
  const cacheKey = JSON.stringify([name, enable, ...args]);

  const { data, error, isValidating, mutate } = useSWR(
    cacheKey,
    () => (enable ? callable(...args) : Promise.resolve(settings?.defaultValue)),
    { ...DEFAULT_SWR_SETTINGS, ...settings?.swrOverrides },
  );

  const oldData = usePrevious(data);

  useErrorSnackbarEffect(settings?.suppressErrors ? undefined : error);

  // if we are still loading do not reset the data to `undefined`, but temporarily used the old data instead.
  // if there is still no data (e.g. the request errored before ever resolving), fall back to `defaultValue`.
  const value = data ?? (isValidating ? oldData : undefined) ?? settings?.defaultValue;
  const resolvedData = settings?.transform ? settings.transform(value) : value;

  return {
    data: resolvedData as ResolvedData<C, N, S>,
    error,
    hasError: !!error,
    isLoading: isValidating,
    mutate,
  };
};
